import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from 'components/dialog';
import {
    AUCTION_PACKAGES_LEARN_MORE_LINK,
    LUXGIVE_LINK,
    THESHAREWAY_LINK,
    WINSPIRE_LINK,
} from 'constants/links';
import AuctionPackagesPrimaryBackground from 'images/auction-partners-dialog-bg-primary.svg';
import AuctionPackagesSecondaryBackground from 'images/auction-partners-dialog-bg-secondary.webp';
import LuxGive from 'images/luxgive.svg?react';
import TheShareWay from 'images/theshareway.svg?react';
import Winspire from 'images/winspire.svg?react';
import { useFlags } from 'launchdarkly-react-client-sdk';

const AuctionPartnersDialog = ({ children, ...props }: Dialog.DialogProps) => {
    const { luxGiveSponsor = false } = useFlags();

    return (
        <Dialog.Root {...props}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content
                    className="max-w-[64rem] bg-cover bg-no-repeat p-10 pb-14"
                    style={{
                        backgroundImage: `url(${AuctionPackagesSecondaryBackground}), url(${AuctionPackagesPrimaryBackground})`,
                        backgroundPosition: 'right 50% center, 0px 0px',
                    }}
                >
                    <Dialog.Body className="relative">
                        <div className="flex flex-col gap-12 text-gray-600">
                            <div className="flex flex-col gap-3 md:max-w-[32rem]">
                                <h1 className="text-4xl font-medium">
                                    Boost your auction with items from our partners 🎁
                                </h1>
                                <p className="text-xl">
                                    With these unique items and inspiring travel packages, you can
                                    attract more bidders and increase your chances of reaching your
                                    fundraising goals.
                                </p>
                                <a
                                    className="flex items-center gap-2 text-gray-600"
                                    href={AUCTION_PACKAGES_LEARN_MORE_LINK}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                >
                                    Learn more{' '}
                                    <FontAwesomeIcon
                                        className="text-gray-700"
                                        icon={faArrowRight}
                                    />
                                </a>
                            </div>
                            <div className="flex flex-col gap-8 md:flex-row">
                                <Box className="flex flex-1 flex-col justify-between gap-5 p-4">
                                    <p className="flex flex-col items-start gap-2">
                                        <Winspire className="min-h-8" />
                                        Winspire offers full-service experiences and travel
                                        packages. Work directly with a Winspire representative to
                                        design experiences best suited to your auctions' audience.
                                    </p>
                                    <Button
                                        variant="solid"
                                        onClick={() =>
                                            window.open(
                                                WINSPIRE_LINK,
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }
                                    >
                                        Explore
                                    </Button>
                                </Box>
                                <Box className="flex flex-1 flex-col justify-between gap-5 p-4">
                                    <p className="flex flex-col items-start gap-2">
                                        <TheShareWay className="min-h-8" />
                                        TheShareWay is a directory of companies offering items that
                                        you can add to your auction.
                                    </p>
                                    <p>
                                        <span className="font-medium">15% off</span> with the code{' '}
                                        <span className="font-medium">GIVEBUTTER</span> at checkout
                                    </p>
                                    <Button
                                        variant="solid"
                                        onClick={() =>
                                            window.open(
                                                THESHAREWAY_LINK,
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }
                                    >
                                        Explore
                                    </Button>
                                </Box>
                                {luxGiveSponsor && (
                                    <Box className="flex flex-1 flex-col justify-between gap-5 p-4">
                                        <p className="flex flex-col items-start gap-2">
                                            <LuxGive className="min-h-8" />
                                            LuxGive offers curated auction packages featuring
                                            exclusive private residences, immersive local
                                            experiences, and premium guest services.
                                        </p>
                                        <Button
                                            variant="solid"
                                            onClick={() =>
                                                window.open(
                                                    LUXGIVE_LINK,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                )
                                            }
                                        >
                                            Explore
                                        </Button>
                                    </Box>
                                )}
                            </div>
                        </div>
                    </Dialog.Body>
                    <Dialog.Close className="[&>svg]:text-white" />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const Root = AuctionPartnersDialog;
const Trigger = Dialog.Trigger;
const AuctionPartnersDialogTrigger = Trigger;

export {
    Root,
    Trigger,
    //
    AuctionPartnersDialog,
    AuctionPartnersDialogTrigger,
};
